import { Directive, ElementRef, HostListener, Input, OnDestroy, inject } from '@angular/core';
import { Overlay, OverlayRef, FlexibleConnectedPositionStrategy, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { SidebarFlyoutComponent } from './sidebar-flyout.component';

@Directive({
  selector: '[appSidebarFlyout]',
  standalone: true,
})
export class SidebarFlyoutDirective implements OnDestroy {
  @Input('appSidebarFlyout') label = '';
  @Input() collapsed = false; // pass sidebar collapsed state

  private overlayRef?: OverlayRef;
  private overlay = inject(Overlay);
  private host = inject(ElementRef<HTMLElement>);
  private closeTimeout?: ReturnType<typeof setTimeout>;

  private positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 0 },
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 0 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 0 },
  ];

  private ensureOverlay() {
    if (this.overlayRef) return;
    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.host)
      .withPositions(this.positions)
      .withPush(false)
      .withFlexibleDimensions(false)
      .withGrowAfterOpen(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'sidebar-flyout-overlay-panel',
    });
  }

  private open() {
    if (!this.collapsed || !this.label) return;
    this.clearCloseDelay();
    this.ensureOverlay();
    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      const portal = new ComponentPortal(SidebarFlyoutComponent);
      const compRef = this.overlayRef.attach(portal);
      compRef.instance.label = this.label;
      compRef.changeDetectorRef.detectChanges();
    }
  }

  private scheduleClose() {
    this.clearCloseDelay();
    this.closeTimeout = setTimeout(() => this.close(), 120);
  }

  private clearCloseDelay() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = undefined;
    }
  }

  private close() {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  @HostListener('mouseenter') onEnter() {
    this.open();
  }
  @HostListener('focusin') onFocus() {
    this.open();
  }
  @HostListener('mouseleave') onLeave() {
    this.scheduleClose();
  }
  @HostListener('focusout') onBlur() {
    this.scheduleClose();
  }

  ngOnDestroy(): void {
    this.clearCloseDelay();
    this.overlayRef?.dispose();
  }
}
